import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import BottomTab from "../../../components/pagination/BottomTab";
import { CardSkeleton, TableSkeleton } from "../../../components/skeleton/ListSkeleton";
import { RevenueKpiKey, UnpaidBucket, UnpaidListData, getRevenue, getRevenueUnpaid } from "../analyticsApi";
import { formatDecimal, formatNumber, formatPct, formatRs, higherIsBetterFor, longDate, pageSlice, seriesIsEmpty } from "../analyticsUtils";
import { useAnalyticsPeriod } from "../useAnalyticsPeriod";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import PeriodPicker from "../components/PeriodPicker";
import KpiCard from "../components/KpiCard";
import EmptyState from "../components/EmptyState";
import { CategoryBarChart, PeriodBarChart } from "../components/charts";
import { CARD, CARD_PAD, Caption, ChartCard, EmptyRow, TD, TD_NUM, TH, TH_NUM, TR, TableCard } from "../components/ui";

const KPIS: { key: RevenueKpiKey; label: string; fmt: (v: number | null) => string }[] = [
  { key: "billed", label: "Billed", fmt: formatRs },
  { key: "collected", label: "Collected", fmt: formatRs },
  { key: "collectionRate", label: "Collection rate", fmt: formatPct },
  { key: "averageBill", label: "Average bill", fmt: formatRs },
  { key: "discount", label: "Discounts", fmt: formatRs },
  { key: "receipts", label: "Receipts", fmt: formatNumber },
];

const BUCKETS: { key: UnpaidBucket; label: string; amount: "b0_30" | "b31_60" | "b60p"; count: "count0_30" | "count31_60" | "count60p"; tone: string }[] = [
  { key: "0_30", label: "0–30 days", amount: "b0_30", count: "count0_30", tone: "text-slate-800 dark:text-slate-100" },
  { key: "31_60", label: "31–60 days", amount: "b31_60", count: "count31_60", tone: "text-amber-600 dark:text-amber-400" },
  { key: "60p", label: "Over 60 days", amount: "b60p", count: "count60p", tone: "text-rose-600 dark:text-rose-400" },
];

const PAGE_SIZE = 20;

export default function AnalyticsRevenue() {
  const period = useAnalyticsPeriod();
  const { data, loading } = useAnalyticsFetch(() => getRevenue({ from: period.from, to: period.to }), [period.from, period.to]);

  const [bucket, setBucket] = useState<UnpaidBucket | null>(null);
  const [unpaid, setUnpaid] = useState<UnpaidListData | null>(null);
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!bucket) return;
    let cancelled = false;
    setUnpaidLoading(true);
    setPage(1);
    getRevenueUnpaid(bucket)
      .then((d) => {
        if (!cancelled) setUnpaid(d);
      })
      .catch((err) => {
        console.error(err?.response || err);
        if (!cancelled) toast.error("Could not load unpaid invoices");
      })
      .finally(() => {
        if (!cancelled) setUnpaidLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bucket]);

  const noData = !loading && data != null && seriesIsEmpty(data.series, ["billed", "collected", "receipts"]);

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <PeriodPicker period={period} />

      {loading || !data ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <CardSkeleton cards={6} height="h-28" />
        </div>
      ) : noData ? (
        <EmptyState />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {KPIS.map((k) => (
              <KpiCard key={k.key} label={k.label} value={k.fmt(data.kpis[k.key]?.current ?? null)} change={data.kpis[k.key]?.change ?? null} higherIsBetter={higherIsBetterFor(k.key)} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <ChartCard title="Billed vs collected" hint={data.period.granularity === "month" ? "Per month" : "Per day"}>
                <PeriodBarChart series={data.series} specs={[{ key: "billed", label: "Billed" }, { key: "collected", label: "Collected" }]} format="rs" />
              </ChartCard>
            </div>
            <ChartCard title="Collected by method">
              <CategoryBarChart
                labels={["Cash", "Card", "Bank transfer", "Online"]}
                values={[data.totals.cash, data.totals.card, data.totals.bankTransfer, data.totals.online]}
                label="Collected"
                format="rs"
                horizontal
              />
              <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {[
                  ["Cash", data.totals.cash],
                  ["Card", data.totals.card],
                  ["Bank transfer", data.totals.bankTransfer],
                  ["Online", data.totals.online],
                ].map(([l, v]) => (
                  <li key={String(l)} className="flex justify-between gap-2">
                    <span className="text-slate-500 dark:text-slate-400">{l}</span>
                    <span className="tabular-nums font-medium">{formatRs(v as number)}</span>
                  </li>
                ))}
              </ul>
            </ChartCard>
          </div>
        </>
      )}

      {data && (
        <>
          <div>
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <h3 className="text-sm font-semibold">Unpaid by age</h3>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {data.unpaidAsOf ? `Snapshot as of ${longDate(data.unpaidAsOf)} · click a card for the live list` : "No snapshot yet"}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {BUCKETS.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => setBucket(b.key)}
                  className={`${CARD} p-4 text-left hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary/30 ${bucket === b.key ? "border-primary/40 dark:border-primary/60" : ""}`}
                >
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{b.label}</p>
                  <p className={`text-2xl font-bold tabular-nums mt-1 ${b.tone}`}>{data.unpaid ? formatRs(data.unpaid[b.amount]) : "—"}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{data.unpaid ? `${formatNumber(data.unpaid[b.count])} invoices` : "Not enough data yet"}</p>
                </button>
              ))}
            </div>
            {data.unpaid && data.unpaid.advancesHeld > 0 && <Caption>Advances held: {formatRs(data.unpaid.advancesHeld)}</Caption>}
          </div>

          {bucket && (
            <TableCard
              title={`Unpaid invoices · ${BUCKETS.find((b) => b.key === bucket)?.label}`}
              hint={unpaid ? `${formatNumber(unpaid.count)} invoices · ${formatRs(unpaid.total)} outstanding (live)` : undefined}
              footer={unpaid ? <BottomTab key={bucket} title="invoices" count={unpaid.count} onPageChange={setPage} pageSize={PAGE_SIZE} /> : undefined}
            >
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-600">
                    <th className={TH}>Invoice</th>
                    <th className={TH}>Patient</th>
                    <th className={TH}>Reg no</th>
                    <th className={TH}>Mobile</th>
                    <th className={TH_NUM}>Due</th>
                    <th className={TH_NUM}>Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                  {unpaidLoading || !unpaid ? (
                    <TableSkeleton rows={5} colSpan={6} />
                  ) : unpaid.list.length === 0 ? (
                    <EmptyRow colSpan={6}>No unpaid invoices in this bucket</EmptyRow>
                  ) : (
                    pageSlice(unpaid.list, page, PAGE_SIZE).map((row) => (
                      <tr key={row.treatmentId} className={TR}>
                        <td className={TD}>{row.invoiceNo || "—"}</td>
                        <td className={TD}>{row.patientName || "—"}</td>
                        <td className={TD}>{row.regNo || "—"}</td>
                        <td className={TD}>{row.mobileNo || "—"}</td>
                        <td className={TD_NUM}>{formatRs(row.due)}</td>
                        <td className={TD_NUM}>{formatNumber(row.days)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableCard>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <TableCard title="Discounts by staff" hint="Who gave discounts in this period">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-600">
                      <th className={TH}>Staff</th>
                      <th className={TH_NUM}>Receipts</th>
                      <th className={TH_NUM}>Discount</th>
                      <th className={TH_NUM}>Per receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                    {data.discountByStaff.length === 0 ? (
                      <EmptyRow colSpan={4}>No discounts in this period</EmptyRow>
                    ) : (
                      data.discountByStaff.map((row, i) => (
                        <tr key={row.employeeId ?? `unknown-${i}`} className={TR}>
                          <td className={TD}>{row.name}</td>
                          <td className={TD_NUM}>{formatNumber(row.receipts)}</td>
                          <td className={TD_NUM}>{formatRs(row.amount)}</td>
                          <td className={TD_NUM}>{row.receipts > 0 ? formatRs(row.amount / row.receipts) : "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </TableCard>
            </div>
            <div className={CARD_PAD}>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Days to paid</p>
              <p className="text-3xl font-bold tabular-nums mt-1">{data.daysToPaidAvg == null ? "—" : `${formatDecimal(data.daysToPaidAvg)} days`}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Average from first receipt to the one that cleared the sheet, for sheets cleared this month</p>
              {data.unpaidAsOf && <Caption>As of {longDate(data.unpaidAsOf)}</Caption>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
